import { DEFAULT_CURRENCY, formatMoney, toMajorUnits } from "@vouch/shared";
import { ArrowDownCircle, ArrowLeftRight, ArrowUpCircle, CalendarClock, Check, PiggyBank, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { QuickAddModal } from "../components/QuickAddModal";
import { Button, Card, Spinner } from "../components/ui";
import { useAccountHistory, useAccounts, useCommittees, useConfirmTransaction, useCreateLoan, useFxRates, useLoans, useRecurringDue, useSkipTransaction, useTransactions } from "../lib/hooks";
import { useAuth } from "../lib/auth";
import { toast } from "../lib/toast";
import { CreateLoanModal } from "./Loans";

const PIE_COLORS = ["#1a2b48", "#2563eb", "#0ea5e9", "#14b8a6", "#f59e0b", "#ec4899", "#8b5cf6"];

export function DashboardPage(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: accounts, isLoading } = useAccounts();
  const { data: transactions } = useTransactions();
  const { data: loans = [] } = useLoans();
  const { data: committees = [] } = useCommittees();
  const { data: history } = useAccountHistory(6);
  const { data: recurringDue = [] } = useRecurringDue();
  const confirmRecurring = useConfirmTransaction();
  const skipRecurring = useSkipTransaction();
  const createLoan = useCreateLoan();
  const [modal, setModal] = useState<null | "income" | "expense" | "transfer">(null);
  const [loanOpen, setLoanOpen] = useState(false);

  const currency = user?.defaultCurrency ?? accounts?.[0]?.currency ?? DEFAULT_CURRENCY;
  const fxCurrencies = useMemo(
    () => [
      ...(accounts ?? []).map((a) => a.currency),
      ...loans.map((l) => l.currency),
    ],
    [accounts, loans],
  );
  const { data: fxRates = {} } = useFxRates(fxCurrencies, currency);
  const convertMinor = (amount: number, from: string): number => {
    if (from === currency) return amount;
    const rate = fxRates[from.toUpperCase()];
    return typeof rate === "number" ? Math.round(amount * rate) : amount;
  };
  const missingRates = fxCurrencies.some((from) => from !== currency && fxRates[from.toUpperCase()] == null);
  const accountCurrencyByName = useMemo(
    () => new Map((accounts ?? []).map((a) => [a.name, a.currency] as const)),
    [accounts],
  );
  const netWorth = useMemo(
    () => (accounts ?? []).reduce((sum, a) => sum + convertMinor(a.cachedBalance, a.currency), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts, currency, fxRates],
  );

  const { lent, borrowed } = useMemo(() => {
    let l = 0;
    let b = 0;
    for (const loan of loans) {
      if (loan.status !== "active" && loan.status !== "overdue") continue;
      const outstanding = convertMinor(loan.outstanding, loan.currency);
      if (loan.lenderUserId === user?.id) l += outstanding;
      else if (loan.borrowerUserId === user?.id) b += outstanding;
    }
    return { lent: l, borrowed: b };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, fxRates, loans, user?.id]);

  const historyData = (history?.points ?? []).map((p) => {
    const row: Record<string, number | string> = { month: String(p.month) };
    let total = 0;
    for (const [k, v] of Object.entries(p)) {
      if (k === "month" || k === "total") continue;
      const converted = convertMinor(v as number, accountCurrencyByName.get(k) ?? currency);
      row[k] = toMajorUnits(converted);
      total += converted;
    }
    row.total = toMajorUnits(total);
    return row;
  });

  const accountData = (accounts ?? [])
    .filter((a) => a.cachedBalance > 0)
    .map((a) => ({ name: a.name, value: toMajorUnits(convertMinor(a.cachedBalance, a.currency)) }));
  const loanData = [
    { name: "Lent out", value: toMajorUnits(lent) },
    { name: "Borrowed", value: toMajorUnits(borrowed) },
  ];
  const cashflowData = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      let income = 0;
      let expense = 0;
      for (const transaction of transactions ?? []) {
        if (transaction.status !== "confirmed") continue;
        const occurred = new Date(transaction.occurredAt);
        const transactionKey = `${occurred.getFullYear()}-${String(occurred.getMonth() + 1).padStart(2, "0")}`;
        if (transactionKey !== key) continue;
        const converted = toMajorUnits(convertMinor(transaction.amount, transaction.currency));
        if (transaction.type === "income") income += converted;
        if (transaction.type === "expense") expense += converted;
      }
      return { month: date.toLocaleDateString(undefined, { month: "short" }), income, expense, net: income - expense };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, currency, fxRates]);
  const currentCashflow = cashflowData.at(-1) ?? { income: 0, expense: 0, net: 0 };
  const savingsRate = currentCashflow.income > 0 ? Math.round((currentCashflow.net / currentCashflow.income) * 100) : null;

  if (isLoading) return <Spinner />;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-brand-500">
          Hi {user?.name?.split(" ")[0] ?? "there"} 👋
        </h1>
        <p className="text-sm text-slate-500">Here's your money at a glance.</p>
      </div>

      {/* Plain div, not Card — Card's base `bg-white` would override `bg-brand-500`
          and render the white net-worth text invisible. */}
      <div className="rounded-2xl bg-brand-500 p-5 text-white shadow-sm">
        <p className="text-sm opacity-80">Total net worth</p>
        <p className="mt-1 text-3xl font-bold">{formatMoney(netWorth, currency)}</p>
        <p className="mt-1 text-xs text-white/70">
          Shown in your default currency{missingRates ? " · one or more live FX rates are unavailable" : " with live FX"}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setModal("income")}>
            <ArrowUpCircle size={16} /> Income
          </Button>
          <Button variant="secondary" onClick={() => setModal("expense")}>
            <ArrowDownCircle size={16} /> Expense
          </Button>
          <Button variant="secondary" onClick={() => setModal("transfer")}>
            <ArrowLeftRight size={16} /> Transfer
          </Button>
          <Button variant="secondary" onClick={() => setLoanOpen(true)}>
            <PiggyBank size={16} /> Loan
          </Button>
        </div>
      </div>

      {/* Net worth over time — directly below the quick actions. */}
      <Card>
        <h2 className="mb-3 font-semibold text-slate-700">Net worth over time</h2>
        {historyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={historyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={48} />
              <Tooltip formatter={(v: number) => formatMoney(Math.round(v * 100), currency)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {(history?.accounts ?? []).map((name, i) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={PIE_COLORS[(i + 1) % PIE_COLORS.length]}
                  strokeWidth={1.5}
                  dot={false}
                />
              ))}
              <Line type="monotone" dataKey="total" stroke="#1a2b48" strokeWidth={3} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-slate-500">Not enough history yet — add some transactions.</p>
        )}
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <p className="text-sm text-slate-500">Money you lent out</p>
          <p className="mt-1 text-2xl font-semibold text-green-600">{formatMoney(lent, currency)}</p>
          <p className="text-xs text-slate-400">outstanding, owed back to you</p>
        </Card>
        <Card>
          <p className="text-sm text-slate-500">Money you borrowed</p>
          <p className="mt-1 text-2xl font-semibold text-red-600">{formatMoney(borrowed, currency)}</p>
          <p className="text-xs text-slate-400">outstanding, you owe</p>
        </Card>
        <Card>
          <p className="text-sm text-slate-500">This month's savings rate</p>
          <p className={`mt-1 text-2xl font-semibold ${savingsRate != null && savingsRate >= 0 ? "text-blue-700" : "text-amber-600"}`}>{savingsRate == null ? "—" : `${savingsRate}%`}</p>
          <p className="text-xs text-slate-400">confirmed income minus expenses</p>
        </Card>
      </div>

      <Card className="border-blue-100 bg-gradient-to-br from-white to-blue-50/70">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700"><CalendarClock size={20} /></span><div><h2 className="font-semibold text-slate-800">Recurring drafts</h2><p className="text-sm text-slate-500">Scheduled entries appear here for you to confirm each cycle.</p></div></div>
          <Button variant="ghost" onClick={() => navigate("/settings")}>Manage schedules</Button>
        </div>
        {recurringDue.length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-slate-200 bg-white/70 px-4 py-5 text-sm text-slate-500">Nothing is due right now. New monthly, weekly, or yearly drafts will appear automatically.</p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white px-4">
            {recurringDue.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div><p className="font-medium text-slate-800">{item.description || `Recurring ${item.type}`}</p><p className="text-xs text-slate-500">Due {new Date(item.occurredAt).toLocaleDateString()} · {formatMoney(item.amount, item.currency)}</p></div>
                <div className="flex gap-2"><Button variant="ghost" disabled={skipRecurring.isPending} onClick={() => void skipRecurring.mutateAsync(item.id).then(() => toast.success("Draft skipped"))}><X size={15} /> Skip</Button><Button disabled={confirmRecurring.isPending} onClick={() => void confirmRecurring.mutateAsync({ id: item.id }).then(() => toast.success("Recurring transaction confirmed")).catch((error) => toast.error(error instanceof Error ? error.message : "Could not confirm"))}><Check size={15} /> Confirm</Button></div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {committees.length > 0 && (
        <Card>
          <h2 className="mb-3 font-semibold text-slate-700">Your committees</h2>
          <ul className="divide-y divide-slate-100">
            {committees.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-slate-700">{c.name}</p>
                  <p className="text-xs text-slate-400">
                    {formatMoney(c.contributionAmount, c.currency)}/{c.frequency} · {c.totalSlots} slots
                  </p>
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${
                    c.status === "active"
                      ? "bg-green-100 text-green-700"
                      : c.status === "completed"
                        ? "bg-slate-100 text-slate-500"
                        : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {c.status}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <h2 className="mb-1 font-semibold text-slate-700">Income and spending</h2>
          <p className="mb-3 text-xs text-slate-400">Confirmed monthly cash flow in {currency}</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={cashflowData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} width={44} />
              <Tooltip formatter={(value: number) => formatMoney(Math.round(value * 100), currency)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="income" fill="#14b8a6" radius={[5, 5, 0, 0]} />
              <Bar dataKey="expense" fill="#f59e0b" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <h2 className="mb-3 font-semibold text-slate-700">Balance by account</h2>
          {accountData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={accountData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                  {accountData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => formatMoney(Math.round(v * 100), currency)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-500">No positive balances to chart yet.</p>
          )}
        </Card>

        <Card>
          <h2 className="mb-3 font-semibold text-slate-700">Lent vs borrowed</h2>
          {lent > 0 || borrowed > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={loanData}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v: number) => formatMoney(Math.round(v * 100), currency)} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  <Cell fill="#16a34a" />
                  <Cell fill="#ef4444" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-500">No active loans to chart.</p>
          )}
        </Card>
      </div>

      <section>
        <h2 className="mb-3 text-lg font-semibold text-slate-700">Recent activity</h2>
        <Card>
          {transactions && transactions.length > 0 ? (
            <ul className="divide-y divide-slate-100">
              {transactions.slice(0, 8).map((t) => (
                <li key={t.id} className="flex items-center justify-between py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-700">
                      {t.description || t.type.replace("_", " ")}
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(t.occurredAt).toLocaleDateString()} · {t.type.replace("_", " ")}
                    </p>
                  </div>
                  <span
                    className={
                      t.type === "income"
                        ? "text-sm font-semibold text-green-600"
                        : t.type === "expense"
                          ? "text-sm font-semibold text-red-600"
                          : "text-sm font-semibold text-slate-600"
                    }
                  >
                    {formatMoney(t.amount, t.currency)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No transactions yet.</p>
          )}
        </Card>
      </section>

      <button
        onClick={() => setModal("expense")}
        className="fixed bottom-6 right-6 flex h-14 w-14 items-center justify-center rounded-full bg-accent-500 text-white shadow-lg hover:bg-accent-600 lg:hidden"
        aria-label="Quick add"
      >
        <Plus size={24} />
      </button>

      <QuickAddModal open={modal !== null} mode={modal ?? "expense"} onClose={() => setModal(null)} />
      {loanOpen && (
        <CreateLoanModal
          accounts={accounts ?? []}
          busy={createLoan.isPending}
          onClose={() => setLoanOpen(false)}
          onSubmit={async (values) => {
            try {
              const result = await createLoan.mutateAsync(values);
              toast.success(result.status === "pending" ? "Loan request sent for approval" : "Loan created");
              setLoanOpen(false);
            } catch (error) {
              toast.error(error instanceof Error ? error.message : "Failed to create loan");
            }
          }}
        />
      )}
    </div>
  );
}
